import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Download, Upload, Trash2, Building2, ShieldAlert, Check, Sparkles, Search, MapPin, ImagePlus, Inbox, X, Utensils, Dumbbell, CheckCircle2, Circle, CircleDot, ListChecks, Radio, Flame } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import OnboardPreviewMap from "@/components/OnboardPreviewMap";
import { UniversityAutocomplete } from "@/components/UniversityAutocomplete";

export default function AdminSchools() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [requests, setRequests] = useState([]);
  const [housingReports, setHousingReports] = useState([]);
  const [json, setJson] = useState("");
  const [busy, setBusy] = useState(false);
  const [osmQuery, setOsmQuery] = useState("");
  const [osmBusy, setOsmBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [photoPanel, setPhotoPanel] = useState(null); // sid of open panel
  const [places, setPlaces] = useState([]);
  const [placeBusy, setPlaceBusy] = useState("");
  const [checklistPanel, setChecklistPanel] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [feedPanel, setFeedPanel] = useState(null);
  const [feedCfg, setFeedCfg] = useState({ provider: "none", vehicles_url: "", trip_updates_url: "", base: "" });
  const [feedBusy, setFeedBusy] = useState(false);
  const [feedTest, setFeedTest] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState("");

  const load = useCallback(() => {
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => {});
    api.get("/campus-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/housing/reports").then((r) => setHousingReports(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const preview = useMemo(() => {
    try {
      const o = JSON.parse(json);
      return o && Array.isArray(o.center) && o.center.length === 2 ? o : null;
    } catch {
      return null;
    }
  }, [json]);

  const previewCount = preview
    ? (preview.buildings?.length || 0) + (preview.amenities?.length || 0) + (preview.bus_stops?.length || 0)
      + (preview.athletics?.length || 0) + (preview.health?.length || 0) + (preview.services?.length || 0)
    : 0;

  const toLogoSrc = (v) => (v ? (v.startsWith("/api/") ? `${API}${v}` : v) : null);
  const logoUrl = useMemo(() => {
    try { return toLogoSrc(JSON.parse(json)?.logo); } catch { return null; }
  }, [json]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/schools/upload-logo", fd);
      let o = {};
      try { o = json.trim() ? JSON.parse(json) : {}; } catch { o = {}; }
      o.logo = r.data.url;
      setJson(JSON.stringify(o, null, 2));
      toast.success("Logo uploaded and added to the draft. Publish to go live.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
    } finally {
      setLogoBusy(false);
      e.target.value = "";
    }
  };

  const openPhotos = async (sid) => {
    if (photoPanel === sid) { setPhotoPanel(null); return; }
    setPhotoPanel(sid); setPlaces([]);
    try {
      const r = await api.get(`/schools/${sid}/places`);
      setPlaces(r.data.places || []);
    } catch { toast.error("Could not load places for this campus."); }
  };

  const uploadPlacePhoto = async (sid, amenityId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPlaceBusy(amenityId);
    try {
      const fd = new FormData();
      fd.append("amenity_id", amenityId);
      fd.append("file", file);
      const r = await api.post(`/schools/${sid}/place-photo`, fd);
      setPlaces((p) => p.map((x) => (x.id === amenityId ? { ...x, photo: r.data.url } : x)));
      toast.success("Photo uploaded — now live in gap recommendations.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setPlaceBusy(""); e.target.value = ""; }
  };

  const clearPlacePhoto = async (sid, amenityId) => {
    try {
      await api.delete(`/schools/${sid}/place-photo/${amenityId}`);
      setPlaces((p) => p.map((x) => (x.id === amenityId ? { ...x, photo: "" } : x)));
      toast.success("Photo removed.");
    } catch { toast.error("Could not remove photo."); }
  };

  const autoFetchPhotos = async (sid) => {
    setPlaceBusy("__auto__");
    try {
      const r = await api.post(`/schools/${sid}/photos`);
      toast.success(`Auto-matched ${r.data.resolved} real photo(s) from Wikimedia.`);
      const res = await api.get(`/schools/${sid}/places`);
      setPlaces(res.data.places || []);
    } catch { toast.error("Auto-fetch failed."); }
    finally { setPlaceBusy(""); }
  };

  const backfillGreekDorms = async (sid) => {
    setBackfillBusy(sid);
    try {
      await api.post(`/schools/${sid}/backfill-greek-dorms`);
      toast.info("Backfilling dorms, Greek houses & apartments… this can take up to a minute.");
      const started = Date.now();
      const poll = setInterval(async () => {
        if (Date.now() - started > 150000) { clearInterval(poll); setBackfillBusy(""); return; }
        try {
          const r = await api.get(`/schools/${sid}/backfill-status`);
          const st = r.data.status;
          if (st === "done") {
            clearInterval(poll); setBackfillBusy("");
            const a = r.data.added || {};
            toast.success(`Added ${a.greek || 0} Greek, ${a.dorms || 0} dorms & ${a.apartments || 0} apartments.`);
            load();
          } else if (st === "error") {
            clearInterval(poll); setBackfillBusy("");
            toast.error(r.data.message || "Backfill failed — try again.");
          }
        } catch { clearInterval(poll); setBackfillBusy(""); toast.error("Backfill failed — try again."); }
      }, 3000);
    } catch (err) {
      setBackfillBusy("");
      toast.error(err?.response?.data?.detail || "Couldn't start backfill.");
    }
  };

  const openChecklist = async (sid) => {
    if (checklistPanel === sid) { setChecklistPanel(null); return; }
    setChecklistPanel(sid); setReadiness(null);
    try {
      const r = await api.get(`/schools/${sid}/readiness`);
      setReadiness(r.data);
    } catch { toast.error("Could not load the checklist."); }
  };

  const refreshChecklist = async (sid) => {
    try {
      const r = await api.get(`/schools/${sid}/readiness`);
      setReadiness(r.data);
    } catch {}
    load(); // refresh row badges
  };

  const uploadLiveLogo = async (sid, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPlaceBusy("__logo__");
    try {
      await api.post(`/schools/${sid}/logo`, (() => { const fd = new FormData(); fd.append("file", file); return fd; })());
      toast.success("Logo set — now live on the login screen.");
      await refreshChecklist(sid);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed.");
    } finally { setPlaceBusy(""); e.target.value = ""; }
  };

  const goToSetup = (sid) => {
    setChecklistPanel(null);
    const el = document.getElementById("campus-setup-panel");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    toast.info("Use Auto-fill or the campus JSON below, then Publish to update this campus.");
  };

  const openFeed = async (sid) => {
    if (feedPanel === sid) { setFeedPanel(null); return; }
    setFeedPanel(sid); setFeedTest(null);
    try {
      const r = await api.get(`/schools/${sid}/realtime-config`);
      setFeedCfg({ provider: r.data.provider || "none", vehicles_url: r.data.vehicles_url || "",
        trip_updates_url: r.data.trip_updates_url || "", base: r.data.base || "" });
    } catch { toast.error("Could not load the live-feed config."); }
  };

  const saveFeed = async (sid) => {
    setFeedBusy(true);
    try {
      await api.put(`/schools/${sid}/realtime-config`, feedCfg);
      toast.success(feedCfg.provider === "none" ? "Live feed disabled (scheduled)." : "Live feed saved — buses go live for students.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed."); }
    finally { setFeedBusy(false); }
  };

  const testFeed = async (sid) => {
    setFeedBusy(true); setFeedTest(null);
    try {
      const r = await api.post(`/schools/${sid}/realtime-test`, feedCfg);
      setFeedTest(r.data);
    } catch (err) { setFeedTest({ live: false, error: err?.response?.data?.detail || "Test failed" }); }
    finally { setFeedBusy(false); }
  };

  const moveMarker = (kind, i, lat, lng) => {
    try {
      const o = JSON.parse(json);
      if (o[kind] && o[kind][i]) {
        o[kind][i].lat = Math.round(lat * 1e6) / 1e6;
        o[kind][i].lng = Math.round(lng * 1e6) / 1e6;
        setJson(JSON.stringify(o, null, 2));
      }
    } catch {}
  };

  if (user && !user.is_admin) {
    return (
      <AppShell>
        <div className="px-4 lg:px-6 py-20 max-w-md mx-auto text-center" data-testid="admin-denied">
          <ShieldAlert size={40} strokeWidth={1.5} className="mx-auto text-brand mb-4" />
          <h1 className="font-display text-2xl font-semibold mb-2">Approved school email required</h1>
          <p className="text-muted-foreground text-sm">
            Campus onboarding is limited to owners and approved school accounts.
          </p>
        </div>
      </AppShell>
    );
  }

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/schools/template");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "school-template.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill it in, then paste or upload below.");
    } catch {
      toast.error("Could not download template.");
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setJson(String(reader.result || ""));
    reader.readAsText(file);
  };

  const autofill = async (q) => {
    const query = (typeof q === "string" ? q : osmQuery).trim();
    if (!query) return;
    setOsmQuery(query);
    setOsmBusy(true);
    try {
      const r = await api.post("/schools/autofill", { query, radius_km: 1.5 });
      setJson(JSON.stringify(r.data.draft, null, 2));
      const c = r.data.counts;
      toast.success(`Loaded ${c.buildings} buildings, ${c.amenities} amenities, ${c.bus_stops} stops, ${c.dorms || 0} dorms from OpenStreetMap. Review, add colors/routes, then Publish.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't auto-fill from OpenStreetMap.");
    } finally {
      setOsmBusy(false);
    }
  };

  const aiEnrich = async () => {
    let draft = {};
    try { draft = json.trim() ? JSON.parse(json) : {}; } catch { draft = {}; }
    const nm = (draft.name || osmQuery).trim();
    if (!nm) { toast.error("Auto-fill from OpenStreetMap first, or type the university name above."); return; }
    setAiBusy(true);
    try {
      const r = await api.post("/schools/ai-enrich", { name: nm, draft });
      setJson(JSON.stringify(r.data.draft, null, 2));
      const e = r.data.enriched;
      toast.success(`AI added ${e.colleges} college tags, ${e.aliases} aliases, ${e.athletics} athletics, ${e.health} health, ${e.services} services, ${e.dorms || 0} dorms, ${e.greek || 0} Greek houses${e.team ? `, team: ${e.team}` : ""}. Review & Publish.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI enrichment failed — try again.");
    } finally {
      setAiBusy(false);
    }
  };

  const fillFromRequest = (req) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    generateFull(req.school_name);
  };

  const generateFull = async (q) => {
    const query = (typeof q === "string" ? q : osmQuery).trim();
    if (!query) { toast.error("Type the university name first."); return; }
    setOsmQuery(query);
    setGenBusy(true);
    try {
      const r = await api.post("/schools/generate", { query, radius_km: 1.5 });
      setJson(JSON.stringify(r.data.draft, null, 2));
      const c = r.data.counts, e = r.data.enriched;
      toast.success(`Generated ${query}: ${c.buildings} buildings, ${e.colleges} college tags, ${e.aliases} aliases, ${e.athletics} athletics, ${e.services} services, ${e.dorms || 0} dorms, ${e.greek || 0} Greek houses${e.team ? `, ${e.team}` : ""}. Review & Publish — photos auto-fetch on publish.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed — try Auto-fill then Enrich separately.");
    } finally {
      setGenBusy(false);
    }
  };

  const updateAliases = (fn) => {
    try { const o = JSON.parse(json); o.aliases = fn({ ...(o.aliases || {}) }); setJson(JSON.stringify(o, null, 2)); } catch {}
  };
  const addAlias = () => updateAliases((a) => ({ ...a, "": preview?.buildings?.[0]?.id || "" }));
  const removeAlias = (k) => updateAliases((a) => { const n = { ...a }; delete n[k]; return n; });
  const setAliasBuilding = (k, bid) => updateAliases((a) => ({ ...a, [k]: bid }));
  const renameAlias = (oldK, raw) => {
    const newK = (raw || "").trim().toLowerCase();
    if (oldK === newK || !newK) return;
    updateAliases((a) => { const v = a[oldK]; const n = { ...a }; delete n[oldK]; n[newK] = v; return n; });
  };

  const resolveRequest = async (rid, status) => {
    try {
      await api.patch(`/campus-requests/${rid}`, { status });
      setRequests((list) => list.filter((r) => r.id !== rid));
      toast.success(status === "done" ? "Marked as done." : "Dismissed.");
    } catch {
      toast.error("Could not update the request.");
    }
  };

  const resolveReport = async (rid, status) => {
    try {
      await api.patch(`/housing/reports/${rid}`, { status });
      setHousingReports((list) => list.filter((r) => r.id !== rid));
      toast.success(status === "resolved" ? "Marked resolved." : "Dismissed.");
    } catch {
      toast.error("Could not update the report.");
    }
  };

  const verifyComplex = async (r) => {
    try {
      await api.post(`/schools/${r.school}/housing/${r.housing_id}/verify`, {
        name: r.name || "", address: r.address || "",
      });
      setHousingReports((list) => list.filter((x) => x.id !== r.id));
      toast.success(`Verified "${r.name}".`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify complex.");
    }
  };

  const removeComplex = async (r) => {
    try {
      await api.delete(`/schools/${r.school}/housing/${r.housing_id}`);
      setHousingReports((list) => list.filter((x) => x.id !== r.id));
      toast.success("Removed the complex.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove complex.");
    }
  };

  const publish = async () => {
    let payload;
    try {
      payload = JSON.parse(json);
    } catch {
      toast.error("That's not valid JSON. Check for missing commas or quotes.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/schools", payload);
      const n = r.data.requesters_notified || 0;
      toast.success(
        n > 0
          ? `Published “${r.data.name}” — live at login. ${n} student${n > 1 ? "s" : ""} who requested it ${n > 1 ? "were" : "was"} matched & marked done.`
          : `Published “${r.data.name}” — it's live at login now.`
      );
      setJson("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not publish — check required fields.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}? Students on it will move back to the default campus.`)) return;
    try {
      await api.delete(`/schools/${s.id}`);
      toast.success(`Removed ${s.name}.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  return (
    <AppShell>
      <div className="px-4 lg:px-6 py-6 pr-16 lg:pr-20">
        <div className="mono text-[11px] tracking-[0.2em] uppercase text-brand">Admin · Campus onboarding</div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mb-1">Manage campuses</h1>
        <p className="text-sm text-muted-foreground mb-6 max-w-xl">
          jeeo is plug-and-play. Download the template, fill in a school's colors, buildings, dining, and bus routes,
          then paste or upload it — the new campus goes live on the login screen instantly.
        </p>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Onboard form */}
          <motion.div id="campus-setup-panel" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-semibold">Add / update a campus</h2>
              <button
                data-testid="download-template-btn"
                onClick={downloadTemplate}
                className="flex items-center gap-1.5 text-xs font-bold bg-secondary hover:bg-brand hover:text-white rounded-md px-3 py-2 transition-colors"
              >
                <Download size={14} strokeWidth={2} /> Template
              </button>
            </div>

            <div className="mb-4 rounded-lg border border-brand/20 bg-brand/5 p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles size={14} strokeWidth={2} className="text-brand" />
                <span className="text-xs font-bold">Auto-fill from OpenStreetMap</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <UniversityAutocomplete
                    testid="osm-query-input"
                    value={osmQuery}
                    onChange={setOsmQuery}
                    onSubmit={autofill}
                    placeholder="e.g. Baylor University"
                  />
                </div>
                <button
                  data-testid="osm-autofill-btn"
                  onClick={autofill}
                  disabled={osmBusy || !osmQuery.trim()}
                  className="flex items-center gap-1.5 bg-brand text-white rounded-md px-3 py-2 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-40 whitespace-nowrap"
                >
                  <Sparkles size={14} strokeWidth={2} /> {osmBusy ? "Fetching…" : "Auto-fill"}
                </button>
              </div>
              <button
                data-testid="generate-full-btn"
                onClick={() => generateFull()}
                disabled={genBusy || !osmQuery.trim()}
                className="mt-2 w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-brand to-[#7c3aed] text-white rounded-md px-3 py-2.5 text-sm font-bold hover:opacity-90 transition-colors disabled:opacity-40"
              >
                <Sparkles size={15} strokeWidth={2} /> {genBusy ? "Generating full campus…" : "Generate full campus (OSM + AI, one click)"}
              </button>
              <p className="text-[11px] text-muted-foreground mt-1.5">Pulls real buildings, dining, libraries, gyms & bus stops. Then set colors, greeting & routes below.</p>
              <button
                data-testid="ai-enrich-btn"
                onClick={aiEnrich}
                disabled={aiBusy}
                className="mt-2 w-full flex items-center justify-center gap-1.5 bg-[#7c3aed] text-white rounded-md px-3 py-2 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-40"
              >
                <Sparkles size={14} strokeWidth={2} /> {aiBusy ? "Enriching with AI…" : "Enrich with AI (colleges, athletics, services & theme)"}
              </button>
              <p className="text-[11px] text-muted-foreground mt-1.5">Fills in building colleges, paste-import aliases, athletics venues + team, health & services, and school colors — all editable before you publish.</p>
            </div>

            <div className="mb-4 rounded-lg border border-border p-3" data-testid="logo-upload-box">
              <div className="flex items-center gap-1.5 mb-2">
                <ImagePlus size={14} strokeWidth={2} className="text-brand" />
                <span className="text-xs font-bold">Campus logo (optional)</span>
              </div>
              <div className="flex items-center gap-3">
                {logoUrl ? (
                  <img src={logoUrl} alt="Campus logo preview" data-testid="logo-preview"
                    className="w-12 h-12 rounded-full object-contain border border-border shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground shrink-0">
                    <ImagePlus size={18} strokeWidth={1.5} />
                  </div>
                )}
                <label className="flex items-center gap-1.5 text-xs font-bold bg-secondary hover:bg-brand hover:text-white rounded-md px-3 py-2 transition-colors cursor-pointer">
                  <Upload size={14} strokeWidth={2} /> {logoBusy ? "Uploading…" : "Upload logo"}
                  <input data-testid="logo-upload-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={uploadLogo} disabled={logoBusy} />
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">PNG, JPG, WEBP or SVG up to 3&nbsp;MB. Shown as a circular badge — a transparent background works best.</p>
            </div>

            <label className="block">
              <span className="mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">School JSON</span>
              <textarea
                data-testid="school-json-input"
                value={json}
                onChange={(e) => setJson(e.target.value)}
                placeholder='{ "id": "unt", "name": "University of North Texas", ... }'
                className="mt-1 w-full h-64 rounded-md border border-border bg-background p-3 font-mono text-xs focus:ring-2 focus:ring-brand outline-none resize-y"
              />
            </label>

            <div className="flex items-center gap-2 mt-3">
              <label className="flex items-center gap-1.5 text-xs font-bold bg-secondary hover:bg-brand hover:text-white rounded-md px-3 py-2 transition-colors cursor-pointer">
                <Upload size={14} strokeWidth={2} /> Upload .json
                <input data-testid="school-file-input" type="file" accept="application/json,.json" className="hidden" onChange={onFile} />
              </label>
              <button
                data-testid="publish-school-btn"
                onClick={publish}
                disabled={busy || !json.trim()}
                className="ml-auto flex items-center gap-2 bg-brand text-white rounded-md px-4 py-2.5 text-sm font-semibold hover:opacity-90 transition-colors disabled:opacity-40"
              >
                <Check size={15} strokeWidth={2} /> {busy ? "Publishing…" : "Publish campus"}
              </button>
            </div>
          </motion.div>

          {/* Existing schools */}
          <div>
            {requests.length > 0 && (
              <div className="mb-6" data-testid="campus-requests-panel">
                <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                  <Inbox size={18} strokeWidth={1.8} className="text-brand" />
                  Campus requests ({requests.length})
                </h2>
                <div className="space-y-3">
                  {[...requests].sort((a, b) => (b.votes || 0) - (a.votes || 0)).map((r) => (
                    <div key={r.id} data-testid={`request-row-${r.id}`}
                      className="bg-white border border-brand/30 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                            {r.school_name}
                            <span data-testid={`request-count-${r.id}`}
                              className="inline-flex items-center gap-1 mono text-[11px] font-bold bg-[#ea580c]/10 text-[#ea580c] rounded-full px-2 py-0.5">
                              <Flame size={11} strokeWidth={2} /> {1 + (r.votes || 0)} requested
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                          {r.note && <p className="text-xs text-muted-foreground mt-1 italic">"{r.note}"</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button data-testid={`request-done-${r.id}`} onClick={() => resolveRequest(r.id, "done")}
                            title="Mark done" className="p-1.5 rounded-md bg-[#16A34A]/10 text-[#16A34A] hover:bg-[#16A34A]/20 transition-colors">
                            <Check size={15} strokeWidth={2} />
                          </button>
                          <button data-testid={`request-dismiss-${r.id}`} onClick={() => resolveRequest(r.id, "dismissed")}
                            title="Dismiss" className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors">
                            <X size={15} strokeWidth={2} />
                          </button>
                        </div>
                      </div>
                      <button data-testid={`request-fill-${r.id}`} onClick={() => fillFromRequest(r)}
                        disabled={genBusy || osmBusy}
                        className="mt-3 w-full flex items-center justify-center gap-1.5 bg-gradient-to-r from-brand to-[#7c3aed] text-white rounded-md py-2 text-xs font-semibold hover:opacity-90 transition-colors disabled:opacity-40">
                        <Sparkles size={13} strokeWidth={2} /> Generate full campus (OSM + AI)
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {housingReports.length > 0 && (
              <div className="mb-6" data-testid="housing-reports-panel">
                <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                  <Inbox size={18} strokeWidth={1.8} className="text-brand" />
                  Housing reports ({housingReports.length})
                </h2>
                <div className="space-y-3">
                  {housingReports.map((r) => (
                    <div key={r.id} data-testid={`housing-report-${r.id}`} className="bg-white border border-amber-300 rounded-xl p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`mono text-[11px] font-bold rounded px-1.5 py-0.5 ${r.kind === "missing" ? "bg-brand/10 text-brand" : "bg-amber-100 text-amber-700"}`}>
                              {r.kind === "missing" ? "MISSING" : "INCORRECT"}
                            </span>
                            <span className="font-semibold text-sm truncate">{r.name || "(unnamed)"}</span>
                          </div>
                          {r.address && <div className="text-xs text-muted-foreground mt-0.5">{r.address}</div>}
                          {r.note && <p className="text-xs text-muted-foreground mt-1 italic">"{r.note}"</p>}
                          {r.kind === "missing" && r.housing_id && (
                            <div className="mono text-[11px] uppercase tracking-widest text-amber-700 mt-1">
                              Auto-added · unverified — visible to students now
                            </div>
                          )}
                          <div className="text-[11px] text-muted-foreground mt-1">— {r.reporter}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {r.kind === "missing" && r.housing_id ? (
                            <>
                              <button data-testid={`report-verify-${r.id}`} onClick={() => verifyComplex(r)}
                                title="Verify & keep this complex"
                                className="text-xs font-bold rounded-md px-2.5 py-1.5 bg-[#16A34A]/10 text-[#16A34A] hover:bg-[#16A34A]/20 transition-colors">
                                Verify
                              </button>
                              <button data-testid={`report-remove-${r.id}`} onClick={() => removeComplex(r)}
                                title="Remove this complex" className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors">
                                <X size={15} strokeWidth={2} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button data-testid={`report-resolve-${r.id}`} onClick={() => resolveReport(r.id, "resolved")}
                                title="Mark resolved" className="p-1.5 rounded-md bg-[#16A34A]/10 text-[#16A34A] hover:bg-[#16A34A]/20 transition-colors">
                                <Check size={15} strokeWidth={2} />
                              </button>
                              <button data-testid={`report-dismiss-${r.id}`} onClick={() => resolveReport(r.id, "dismissed")}
                                title="Dismiss" className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors">
                                <X size={15} strokeWidth={2} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h2 className="font-display text-lg font-semibold mb-3">Live campuses ({schools.length})</h2>
            <div className="space-y-3" data-testid="school-list">
              {schools.map((s) => (
                <div key={s.id} data-testid={`school-row-${s.id}`} className="bg-white border border-border rounded-xl">
                  <div className="p-4 flex flex-col gap-3">
                  <div className="flex items-center gap-3 min-w-0 w-full">
                  {s.logo ? (
                    <img src={toLogoSrc(s.logo)} alt={s.name}
                      className="w-9 h-9 rounded-full object-contain border border-border shrink-0" />
                  ) : (
                    <span className="w-9 h-9 rounded-full flex items-center justify-center text-white text-[11px] font-bold mono shrink-0"
                      style={{ background: s.theme?.primary }}>
                      {s.short}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{s.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                      <Building2 size={11} strokeWidth={1.5} className="shrink-0" />
                      <span className="truncate">{s.city || "—"} · “{s.theme?.greeting}”</span>
                    </div>
                  </div>
                  {typeof s.readiness === "number" && (
                    <span data-testid={`readiness-badge-${s.id}`}
                      title={`Onboarding ${s.readiness}% complete`}
                      className={`mono text-[11px] font-bold px-2 py-1 rounded shrink-0 ${
                        s.readiness === 100 ? "bg-[#16A34A]/10 text-[#166534]"
                          : s.readiness >= 50 ? "bg-[#F59E0B]/15 text-[#92400e]"
                          : "bg-destructive/10 text-[#991b1b]"
                      }`}>
                      {s.readiness}%
                    </span>
                  )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <button
                    data-testid={`checklist-${s.id}`}
                    onClick={() => openChecklist(s.id)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
                      checklistPanel === s.id ? "bg-brand text-white" : "border border-border hover:border-brand"
                    }`}
                    title="Onboarding checklist"
                  >
                    <ListChecks size={14} strokeWidth={1.8} /> Checklist
                  </button>
                  <button
                    data-testid={`live-feed-${s.id}`}
                    onClick={() => openFeed(s.id)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
                      feedPanel === s.id ? "bg-brand text-white" : "border border-border hover:border-brand"
                    }`}
                    title="Live bus feed"
                  >
                    <Radio size={14} strokeWidth={1.8} /> Live feed
                  </button>
                  <button
                    data-testid={`manage-photos-${s.id}`}
                    onClick={() => openPhotos(s.id)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md transition-colors ${
                      photoPanel === s.id ? "bg-brand text-white" : "border border-border hover:border-brand"
                    }`}
                    title="Manage place photos"
                  >
                    <ImagePlus size={14} strokeWidth={1.8} /> Photos
                  </button>
                  <button
                    data-testid={`backfill-greek-dorms-${s.id}`}
                    onClick={() => backfillGreekDorms(s.id)}
                    disabled={backfillBusy === s.id}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-border hover:border-brand transition-colors disabled:opacity-50"
                    title="Auto-add on-campus dorms (OpenStreetMap), Greek houses & off-campus apartments (AI)"
                  >
                    <Sparkles size={14} strokeWidth={1.8} /> {backfillBusy === s.id ? "Adding…" : "Dorms, Greek & Apts"}
                  </button>
                  {s.id !== "tamu" && (
                    <button
                      data-testid={`delete-school-${s.id}`}
                      onClick={() => remove(s)}
                      className="p-2 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
                      title={`Delete ${s.name}`}
                    >
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  )}
                  </div>
                  </div>

                  {photoPanel === s.id && (
                    <div className="border-t border-border p-4" data-testid={`photo-panel-${s.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                        <p className="text-xs text-muted-foreground max-w-md">
                          Upload the official photo for each dining hall, library or rec center — it shows on the “Gap detected” cards. Or auto-match free real photos from Wikimedia.
                        </p>
                        <button data-testid={`auto-fetch-${s.id}`} onClick={() => autoFetchPhotos(s.id)}
                          disabled={placeBusy === "__auto__"}
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-border hover:border-brand transition-colors disabled:opacity-50">
                          <Sparkles size={14} strokeWidth={1.8} /> {placeBusy === "__auto__" ? "Matching…" : "Auto-fetch (Wikimedia)"}
                        </button>
                      </div>
                      {places.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-3">Loading places…</div>
                      ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {places.map((pl) => (
                            <div key={pl.id} data-testid={`place-photo-${pl.id}`} className="border border-border rounded-lg overflow-hidden">
                              <div className="h-24 bg-secondary relative">
                                {pl.photo ? (
                                  <img src={toLogoSrc(pl.photo)} alt={pl.name} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/50">
                                    {pl.type === "dining" ? <Utensils size={26} strokeWidth={1.5} />
                                      : pl.type === "library" ? <Building2 size={26} strokeWidth={1.5} />
                                      : <Dumbbell size={26} strokeWidth={1.5} />}
                                  </div>
                                )}
                                {pl.photo && (
                                  <button data-testid={`clear-photo-${pl.id}`} onClick={() => clearPlacePhoto(s.id, pl.id)}
                                    title="Remove photo" className="absolute top-1.5 right-1.5 p-1 rounded bg-black/60 text-white hover:bg-destructive transition-colors">
                                    <X size={12} strokeWidth={2} />
                                  </button>
                                )}
                              </div>
                              <div className="p-2.5">
                                <div className="text-xs font-semibold truncate">{pl.name}</div>
                                <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">{pl.type}</div>
                                <label className="flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2 py-1.5 rounded-md border border-border hover:border-brand cursor-pointer transition-colors">
                                  <Upload size={12} strokeWidth={2} /> {placeBusy === pl.id ? "Uploading…" : (pl.photo ? "Replace" : "Upload")}
                                  <input data-testid={`upload-photo-input-${pl.id}`} type="file" accept="image/png,image/jpeg,image/webp"
                                    className="hidden" disabled={placeBusy === pl.id} onChange={(e) => uploadPlacePhoto(s.id, pl.id, e)} />
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {checklistPanel === s.id && (
                    <div className="border-t border-border p-4" data-testid={`checklist-panel-${s.id}`}>
                      {!readiness ? (
                        <div className="text-xs text-muted-foreground py-3">Loading checklist…</div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 mb-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between text-xs mb-1">
                                <span className="font-semibold">Onboarding · {readiness.completed}/{readiness.required} required steps</span>
                                <span className="mono font-bold text-brand" data-testid={`readiness-percent-${s.id}`}>{readiness.percent}%</span>
                              </div>
                              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                                <div className="h-full bg-brand transition-all" style={{ width: `${readiness.percent}%` }} />
                              </div>
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            {readiness.items.map((it) => (
                              <div key={it.key} data-testid={`checklist-item-${it.key}`}
                                className="flex items-center gap-2.5 py-1.5">
                                {it.status === "done"
                                  ? <CheckCircle2 size={18} strokeWidth={2} className="text-[#16A34A] shrink-0" />
                                  : it.status === "progress"
                                  ? <CircleDot size={18} strokeWidth={2} className="text-[#F59E0B] shrink-0" />
                                  : <Circle size={18} strokeWidth={2} className="text-[#DC2626] shrink-0" />}
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold flex items-center gap-2">
                                    {it.label}
                                    {typeof it.count === "number" && (
                                      <span className="mono text-[11px] text-muted-foreground">
                                        {it.total != null ? `${it.count}/${it.total}` : it.count}
                                      </span>
                                    )}
                                  </div>
                                  {it.status !== "done" && <div className="text-xs text-muted-foreground">{it.hint}</div>}
                                </div>
                                {it.status !== "done" && (
                                  it.key === "logo" ? (
                                    <label data-testid={`checklist-logo-action-${s.id}`}
                                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border hover:border-brand transition-colors shrink-0 cursor-pointer">
                                      {placeBusy === "__logo__" ? "Uploading…" : "Upload"}
                                      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                                        className="hidden" disabled={placeBusy === "__logo__"}
                                        onChange={(e) => uploadLiveLogo(s.id, e)} />
                                    </label>
                                  ) : it.key === "photos" ? (
                                    <button data-testid={`checklist-photos-action-${s.id}`}
                                      onClick={() => { openPhotos(s.id); setChecklistPanel(null); }}
                                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border hover:border-brand transition-colors shrink-0">
                                      Manage
                                    </button>
                                  ) : (
                                    <button data-testid={`checklist-setup-action-${it.key}`}
                                      onClick={() => goToSetup(s.id)}
                                      className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-border hover:border-brand transition-colors shrink-0">
                                      Set up
                                    </button>
                                  )
                                )}
                              </div>
                            ))}
                          </div>
                          {readiness.percent === 100 && (
                            <div className="mt-3 text-xs font-semibold text-[#166534] bg-[#16A34A]/10 rounded-md px-3 py-2 flex items-center gap-1.5">
                              <Check size={14} strokeWidth={2.5} /> This campus is fully onboarded and ready for students.
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {feedPanel === s.id && (
                    <div className="border-t border-border p-4 space-y-3" data-testid={`feed-panel-${s.id}`}>
                      <p className="text-xs text-muted-foreground max-w-lg">
                        Connect a live bus feed for this campus. Paste a <b>GTFS-Realtime</b> VehiclePositions URL, a <b>DoubleMap</b> base URL, or use <b>Demo</b> to preview moving buses. Leave as <b>Scheduled</b> if there's no feed yet.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {[["none", "Scheduled"], ["gtfs-rt", "GTFS-Realtime"], ["doublemap", "DoubleMap"], ["demo", "Demo"]].map(([val, lbl]) => (
                          <button key={val} data-testid={`feed-provider-${val}-${s.id}`}
                            onClick={() => { setFeedCfg((c) => ({ ...c, provider: val })); setFeedTest(null); }}
                            className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                              feedCfg.provider === val ? "bg-brand text-white border-brand" : "border-border hover:border-brand"
                            }`}>
                            {lbl}
                          </button>
                        ))}
                      </div>
                      {feedCfg.provider === "gtfs-rt" && (
                        <div className="space-y-2">
                          <input data-testid={`feed-vehicles-url-${s.id}`} value={feedCfg.vehicles_url}
                            onChange={(e) => setFeedCfg((c) => ({ ...c, vehicles_url: e.target.value }))}
                            placeholder="VehiclePositions feed URL (.pb) — required"
                            className="w-full text-sm border border-border rounded-md px-3 py-2 mono" />
                          <input data-testid={`feed-trip-url-${s.id}`} value={feedCfg.trip_updates_url}
                            onChange={(e) => setFeedCfg((c) => ({ ...c, trip_updates_url: e.target.value }))}
                            placeholder="TripUpdates feed URL (.pb) — optional"
                            className="w-full text-sm border border-border rounded-md px-3 py-2 mono" />
                        </div>
                      )}
                      {feedCfg.provider === "doublemap" && (
                        <input data-testid={`feed-base-${s.id}`} value={feedCfg.base}
                          onChange={(e) => setFeedCfg((c) => ({ ...c, base: e.target.value }))}
                          placeholder="https://agency.doublemap.com"
                          className="w-full text-sm border border-border rounded-md px-3 py-2 mono" />
                      )}
                      <div className="flex flex-wrap items-center gap-2">
                        <button data-testid={`feed-save-${s.id}`} onClick={() => saveFeed(s.id)} disabled={feedBusy}
                          className="text-xs font-semibold px-4 py-2 rounded-md bg-brand text-white disabled:opacity-50">
                          {feedBusy ? "Saving…" : "Save"}
                        </button>
                        {feedCfg.provider !== "none" && (
                          <button data-testid={`feed-test-${s.id}`} onClick={() => testFeed(s.id)} disabled={feedBusy}
                            className="text-xs font-semibold px-4 py-2 rounded-md border border-border hover:border-brand disabled:opacity-50">
                            Test feed
                          </button>
                        )}
                        {feedTest && (
                          <span data-testid={`feed-test-result-${s.id}`} className={`text-xs font-semibold px-2.5 py-1 rounded ${
                            feedTest.live ? "bg-[#16A34A]/10 text-[#166534]" : "bg-destructive/10 text-[#991b1b]"
                          }`}>
                            {feedTest.live ? `🟢 Live · ${feedTest.count} buses (${feedTest.source})` : `Not live${feedTest.error ? ` — ${feedTest.error}` : ""}`}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {preview && previewCount > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="mt-6 bg-white border border-border rounded-xl p-5" data-testid="preview-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <MapPin size={16} strokeWidth={1.8} className="text-brand" />
                <h2 className="font-display text-lg font-semibold">Preview & correct on map</h2>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#500000" }} /> Academic</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#1d4ed8" }} /> Library</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#F59E0B" }} /> Dining</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#16a34a" }} /> Rec</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#0a0a0a" }} /> Bus stop</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#7c3aed" }} /> Athletics</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#0891b2" }} /> Health</span>
                <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#ea580c" }} /> Services</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              {previewCount} pins from your draft. <span className="font-medium text-foreground">Drag any pin</span> to fix its position — the JSON above updates automatically.
            </p>
            <OnboardPreviewMap draft={preview} onMove={moveMarker} />
            {preview.buildings?.length > 0 && (
              <div className="mt-4 border-t border-border pt-4" data-testid="aliases-editor">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-semibold">Building aliases (paste-import)</span>
                    <p className="text-[11px] text-muted-foreground">Codes/nicknames students paste (e.g. "hrbb") that map to a building.</p>
                  </div>
                  <button data-testid="alias-add-btn" onClick={addAlias}
                    className="text-xs font-semibold px-3 py-1.5 rounded-md border border-border hover:border-brand transition-colors shrink-0">
                    + Add alias
                  </button>
                </div>
                <div className="space-y-2">
                  {Object.entries(preview.aliases || {}).map(([k, bid], idx) => (
                    <div key={`${idx}-${k}`} className="flex items-center gap-2" data-testid={`alias-row-${idx}`}>
                      <input defaultValue={k} onBlur={(e) => renameAlias(k, e.target.value)}
                        placeholder="alias e.g. hrbb"
                        className="w-40 text-sm rounded-md border border-border px-2 py-1.5 mono focus:border-brand outline-none" />
                      <span className="text-muted-foreground">→</span>
                      <select value={bid} onChange={(e) => setAliasBuilding(k, e.target.value)}
                        className="flex-1 text-sm rounded-md border border-border px-2 py-1.5 focus:border-brand outline-none">
                        {preview.buildings.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                      </select>
                      <button data-testid={`alias-remove-${idx}`} onClick={() => removeAlias(k)}
                        className="p-1.5 rounded-md text-destructive hover:bg-destructive/10 transition-colors shrink-0">
                        <X size={15} strokeWidth={2} />
                      </button>
                    </div>
                  ))}
                  {Object.keys(preview.aliases || {}).length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No aliases yet — AI enrichment adds these, or click "+ Add alias".</p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </AppShell>
  );
}
